import { FundAdditionalIncomeAndExpenditureModel } from '@/features/project/projects/_models/fund-additiona-income-and-expenditure';
import { PaymentModel } from '@/features/project/projects/_models/payment';
import {
  FundAdditionalAttributesModel,
  ProjectBaseModel,
  candidateReasonOfInspectionByExternalExpert,
} from '@/features/project/projects/_models/project';
import { reviewTypeEnum, sheetTypeEnum } from '@/utils/enum';
import { z } from 'zod';

/// //////////////////////////////////////////////////
/// Enum
/// /////////////////////////////////////////////////

export const ReportReviewSheetListModel = z
  .object({
    id: z.string().uuid(),
    sheetType: sheetTypeEnum,
    fiscalYear: z.number().int(),
    name: z.string().min(1),
    overview: z.string().nullish(),
    isLocked: z.boolean(),
    isPublished: z.boolean(),
    startYear: z.number().int().nullable(),
    endYear: z.number().int().nullable(),
    purpose: z.string().nullish(),
    task: z.string().nullish(),
    projectNumber: z.number().nullable(),
    ministryName: z.string().nullable(),
    organization: z
      .object({ flatten: z.object({ names: z.array(z.string()) }) })
      .nullable(),
    finalizedAmountForInitialBudget: z.number().nullable(),
    negativeFinalizedAmountForInitialBudgetCount: z.number().nullish(),
    previousYearExecutionAmount: z.number().nullable(),
    negativePreviousYearExecutionAmountCount: z.number().nullish(),
    previousYearExecutionRate: z.number().nullable(),
    finalizedAmountForSupplementaryBudget: z.number().nullable(),
    negativeFinalizedAmountForSupplementaryBudgetCount: z.number().nullish(),
    finalizedAmountForCarriedForward: z.number().nullable(),
    negativeFinalizedAmountForCarriedForwardCount: z.number().nullish(),
    finalizedAmountForReserve: z.number().nullable(),
    negativeFinalizedAmountForReserveCount: z.number().nullish(),
    finalizedAmount: z.number().nullable(),
    negativeFinalizedAmountCount: z.number().nullish(),
    requestedAmount: z.number().nullable(),
    negativeRequestedAmountCount: z.number().nullish(),
    paymentData: z
      .object({
        name: PaymentModel.shape.name.nullable(),
        amount: z.number().nullable(),
        negativeAmountCount: z.number().nullish(),
      })
      .optional(),
    skipAdministrativeReformTeamEvaluation:
      ProjectBaseModel.shape.skipAdministrativeReformTeamEvaluation,
    inspectionByExternalExpertReviewType: reviewTypeEnum.nullable(),
    candidateReasonsOfInspectionByExternalExpert:
      candidateReasonOfInspectionByExternalExpert.array().nullable(),
    inspectionByExternalExpertLastImplementedFiscalYear: z.number().nullish(),
  })
  .array();

export const ReportSegmentSheetListModel = z
  .object({
    id: z.string().uuid(),
    sheetType: sheetTypeEnum,
    fiscalYear: z.number().int(),
    name: z.string().min(1),
    overview: z.string().nullish(),
    isLocked: z.boolean(),
    isPublished: z.boolean(),
    startYear: z.number().int().nullable(),
    endYear: z.number().int().nullable(),
    purpose: z.string().nullish(),
    task: z.string().nullish(),
    projectNumber: z.number().nullable(),
    ministryName: z.string().nullable(),
    organization: z
      .object({
        flatten: z.object({
          names: z.array(z.string()),
        }),
      })
      .nullable(),
    finalizedAmountForInitialBudget: z.number().nullable(),
    negativeFinalizedAmountForInitialBudgetCount: z.number().nullish(),
    previousYearExecutionAmount: z.number().nullable(),
    negativePreviousYearExecutionAmountCount: z.number().nullish(),
    previousYearExecutionRate: z.number().nullable(),
    finalizedAmountForSupplementaryBudget: z.number().nullable(),
    negativeFinalizedAmountForSupplementaryBudgetCount: z.number().nullish(),
    requestedAmount: z.number().nullable(),
    negativeRequestedAmountCount: z.number().nullish(),
    paymentData: z
      .object({
        name: PaymentModel.shape.name.nullable(),
        amount: z.number().nullable(),
        negativeAmountCount: z.number().nullish(),
      })
      .optional(),
  })
  .array();

export const ReportFundSheetListModel = z
  .object({
    id: z.string().uuid(),
    sheetType: sheetTypeEnum,
    fiscalYear: z.number().int(),
    name: z.string().min(1),
    overview: z.string().nullish(),
    isLocked: z.boolean(),
    isPublished: z.boolean(),
    startYear: z.number().int().nullable(),
    endYear: z.number().int().nullable(),
    purpose: z.string().nullish(),
    task: z.string().nullish(),
    projectNumber: z.number().nullable(),
    ministryName: z.string().nullable(),
    organization: z
      .object({
        flatten: z.object({
          names: z.array(z.string()),
        }),
      })
      .nullable(),
    paymentData: z
      .object({
        name: PaymentModel.shape.name.nullable(),
        amount: z.number().nullable(),
        negativeAmountCount: z.number().nullish(),
      })
      .optional(),
    fundAdditionalAttributes: FundAdditionalAttributesModel.nullable(),
    fundAdditionalIncomeAndExpenditure:
      FundAdditionalIncomeAndExpenditureModel.nullable(),
    ownershipRatio: z.number().nullable(),
    previousYearTotalIncome: z.number().nullable(),
    negativePreviousYearTotalIncomeCount: z.number().nullish(),
    previousYearTotalExpense: z.number().nullable(),
    negativePreviousYearTotalExpenseCount: z.number().nullish(),
    previousYearDivergenceRate: z.number().nullable(),
    previousYearAdministrativeExpenseRate: z.number().nullable(),
  })
  .array();
